// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Mise en page des pages « documents » — celles qui se lisent, par opposition
// aux écrans qui s'utilisent : les 5 pages légales (`LegalPage.js`) et la page
// d'aide (`HelpPage.js`).
//
// Ces composants vivaient dans `LegalPage.js`, sans être exportés. Ils en ont
// été sortis le 31/08/2026 à l'arrivée de `/aide`, pour que les deux pages
// rendent RIGOUREUSEMENT la même chose : `/aide` reformule en langage métier un
// dispatch qui est contractuel (SLA art. 8, mentions légales art. 10), et deux
// mises en page qui divergent donneraient à lire deux documents là où il n'y en
// a qu'un. ⛔ Aucun texte n'a été déplacé ici — que de la présentation.

import React from 'react';
import { Link } from 'wouter';
import { useAuth } from '../context/AuthContext';

// En-tête commun aux pages documentaires. ⚠️ Il DOIT refléter la session.
//
// Il affichait « Connexion » en toutes circonstances, sur les cinq pages
// légales comme sur `/aide`. Un utilisateur connecté y lisait donc
// « Connexion » en haut à droite — soit « vous n’êtes pas connecté » — alors
// qu’il l’était, et que le formulaire d’assistance de `/aide` lui affichait
// ses propres analyses. Signalé par Marc le 31/08/2026, sur `/aide` puis
// sur `/cgv`. Corrigé ICI, donc pour les six pages à la fois.
//
// 💣 `isAuthenticated` est une FONCTION (`AuthContext.js:92`), pas un booléen.
// Déstructurée sans être appelée, elle vaut TOUJOURS vrai — c’est la première
// version de ce correctif, qui affichait « Retour à l’application » même
// déconnecté. Tous les autres consommateurs l’appellent : `ProtectedRoute.js:16`,
// `Header.js:94`.
//
// ⚠️ Elle ne prouve QUE la présence d’un jeton, jamais sa validité. C’est assez
// pour un lien de navigation — au pire il mène à /dashboard, qui renverra vers
// /login — mais PAS pour décider d’afficher un formulaire : voir la sonde de
// session de `FormulaireAide` dans `pages/HelpPage.js`.
export const EnTeteDocument = () => {
  const { isAuthenticated } = useAuth();
  const connecte = isAuthenticated();
  return (
    <header className="bg-white border-b border-gray-200 py-4 px-6">
      <div className="max-w-3xl mx-auto flex items-center justify-between">
        <Link href="/" className="text-xl font-bold text-gray-800 hover:text-blue-600">
          Normascan
        </Link>
        {connecte ? (
          <Link href="/dashboard" className="text-sm text-gray-600 hover:text-blue-600">
            Retour à l’application
          </Link>
        ) : (
          <Link href="/login" className="text-sm text-gray-600 hover:text-blue-600">
            Connexion
          </Link>
        )}
      </div>
    </header>
  );
};

export const Article = ({ n, title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-blue-900 border-b-2 border-blue-100 pb-1 mb-3">
      {n ? `Article ${n} — ${title}` : title}
    </h2>
    <div className="space-y-3 text-gray-700 leading-relaxed text-sm">{children}</div>
  </section>
);

export const Sub = ({ title, children }) => (
  <div className="mb-3">
    {title && <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>}
    <div className="space-y-2">{children}</div>
  </div>
);

export const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export const DataTable = ({ headers, rows }) => (
  <div className="overflow-x-auto my-3">
    <table className="min-w-full text-xs border border-gray-200">
      <thead className="bg-blue-900 text-white">
        <tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            {row.map((cell, j) => <td key={j} className="px-3 py-2 border-t border-gray-200 align-top">{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Callout = ({ children, tone = 'blue' }) => {
  const tones = {
    blue:   'bg-blue-50 border-blue-400 text-blue-900',
    amber:  'bg-amber-50 border-amber-400 text-amber-900',
    red:    'bg-red-50 border-red-400 text-red-900',
  };
  return <div className={`border-l-4 rounded p-4 text-sm ${tones[tone]}`}>{children}</div>;
};
